import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChildren,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../util/s25.resource";
import { ServiceHourEntry, ServiceHourFilter, ServiceHoursService, ServiceHoursStatus } from "./service.hours.service";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { ServiceHoursFormComponent } from "./service.hours.form.component";
import { S25SidebarComponent } from "../../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../../s25-sidebar/s25.sidebar.store";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25Util } from "../../../util/s25-util";
import { Item } from "../../../pojo/Item";
import { StateStore } from "../../../services/state/state.store";
import { ServiceHourEntryComponent } from "./service.hour.entry.component";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { Debounce } from "../../../decorators/debounce.decorator";
import { UserStore } from "../../../stores/user.store";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Bind } from "../../../decorators/bind.decorator";
import { SheetEntry } from "../../s25-spreadsheet/types/sheet.js.types.expanded";
import { SpreadSheetService } from "../../s25-spreadsheet/spreadsheet.service";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import { S25SelectEnterOpensDirective } from "../../../standalone/s25.select.enter.opens.directive";

type ServiceHoursViewFilter = {
    status: NonNullable<ServiceHourFilter["status"]>;
    dateRange: NonNullable<ServiceHourFilter["dateRange"]>;
    startDate?: string | number | Date | undefined;
    endDate?: string | number | Date | undefined;
    searchTerm: string;
    organizationId?: number;
    contactId?: number;
};

const SERVICE_HOUR_STATUS_ORDER: Record<ServiceHoursStatus, number> = {
    [ServiceHoursStatus.Pending]: 0,
    [ServiceHoursStatus.Approved]: 1,
    [ServiceHoursStatus.Denied]: 2,
};

//Sort by Status (pending, approved, denied) then submit date - newest first.
function compareServiceHourEntries(left: ServiceHourEntry, right: ServiceHourEntry): number {
    const leftStatusOrder = SERVICE_HOUR_STATUS_ORDER[left.status] ?? 9;
    const rightStatusOrder = SERVICE_HOUR_STATUS_ORDER[right.status] ?? 9;
    if (leftStatusOrder !== rightStatusOrder) {
        return leftStatusOrder - rightStatusOrder;
    }
    return new Date(right.createDt).getTime() - new Date(left.createDt).getTime();
}

@TypeManagerDecorator("s25-ng-se-service-hours")
@Component({
    selector: "s25-ng-se-service-hours",
    template: `
        <div class="my-hours-container">
            <header class="hours-header">
                <div class="header-left">
                    <h1 class="page-title">{{ adminMode() ? "All Service Hours" : "Service Hours" }}</h1>
                </div>
                @if (filteredHours().length) {
                    <s25-ng-button [type]="'outline'" [onClick]="onExport">Export</s25-ng-button>
                }
                @if (!adminMode() && canLogHours()) {
                    <button class="aw-button aw-button--outline" type="button" (click)="sidebarStore.toggle()">
                        <s25-ng-icon [type]="'plus'" [size]="16" [ariaLabel]="false" />
                        <span>Log Hours</span>
                    </button>
                }
            </header>

            @if (!adminMode()) {
                <s25-ng-sidebar [title]="'Log Service Hours'" [mode]="'overlay'" [width]="500">
                    @if (sidebarStore.isOpen()) {
                        <service-hours-form (refresh)="reload()"></service-hours-form>
                    }
                </s25-ng-sidebar>
            }

            <div class="stats-summary">
                <div class="stat-card approved">
                    <s25-ng-icon [type]="'checkCircle'" [size]="24" [ariaLabel]="false" />
                    <div class="stat-info">
                        <span class="stat-value">{{ totalApprovedHours() }}</span>
                        <span class="stat-label">Approved Hours</span>
                    </div>
                </div>
                <div class="stat-card pending">
                    <s25-ng-icon [type]="'clock'" [size]="24" [ariaLabel]="false" />
                    <div class="stat-info">
                        <span class="stat-value">{{ totalPendingHours() }}</span>
                        <span class="stat-label">Pending Hours</span>
                    </div>
                </div>
                <div class="stat-card denied">
                    <div class="stat-info">
                        <span class="stat-value">{{ totalDeniedHours() }}</span>
                        <span class="stat-label">Denied Hours</span>
                    </div>
                </div>
                <div class="stat-card total">
                    <div class="stat-info">
                        <span class="stat-value">{{ totalHours() }}</span>
                        <span class="stat-label">Total Hours</span>
                    </div>
                </div>
            </div>

            <div class="filters-container">
                <div class="search-wrapper">
                    <s25-ng-icon [type]="'search'" [size]="18" class="search-icon" [ariaLabel]="false" />
                    <input
                        type="text"
                        class="search-input"
                        placeholder="Search by organization, contact, or activity..."
                        [ngModel]="filterOptions().searchTerm"
                        (ngModelChange)="onFilterUpdate('searchTerm', $event); onKeywordSearch()"
                    />
                    @if (filterOptions().searchTerm) {
                        <button
                            class="clear-btn"
                            (click)="onFilterUpdate('searchTerm', ''); reload()"
                            type="button"
                            aria-label="Clear search"
                        >
                            <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                        </button>
                    }
                </div>

                <div class="filter-controls">
                    @if (adminMode()) {
                        <div class="admin-filter-picker">
                            <s25-ng-dropdown-search-criteria
                                class=""
                                [type]="'studentEngagementOrganizations'"
                                [value]="selectedOrganization()"
                                (valueChange)="onOrganizationFilterChange($event)"
                                [placeholder]="'All organizations'"
                            />
                        </div>
                        <div class="admin-filter-picker">
                            <s25-ng-dropdown-search-criteria
                                class=""
                                [type]="'contacts'"
                                [value]="selectedContact()"
                                (valueChange)="onContactFilterChange($event)"
                                [placeholder]="'All users'"
                            />
                        </div>
                    }

                    <select
                        class="filter-select"
                        s25-ng-select-enter-opens
                        aria-label="Filter by status"
                        [ngModel]="filterOptions().status"
                        (ngModelChange)="onFilterUpdate('status', $event); reload()"
                    >
                        <option value="all">All Statuses</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="denied">Denied</option>
                    </select>

                    <select
                        class="filter-select"
                        s25-ng-select-enter-opens
                        aria-label="Filter by date range"
                        [ngModel]="filterOptions().dateRange"
                        (ngModelChange)="onFilterUpdate('dateRange', $event); onDateRangeChange()"
                    >
                        <option value="all">All Time</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="semester">This Semester</option>
                        <option value="year">This Year</option>
                        <option value="custom">Custom Range</option>
                    </select>

                    @if (filterOptions().dateRange === "custom") {
                        @let bothDatesEntered = !!filterOptions().startDate && !!filterOptions().endDate;
                        <s25-datepicker
                            [value]="filterOptions().startDate"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [placeholder]="'Start date'"
                            (valueChange)="onFilterUpdate('startDate', $event); bothDatesEntered && reload()"
                        ></s25-datepicker>

                        <s25-datepicker
                            [value]="filterOptions().endDate"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [placeholder]="'End date'"
                            (valueChange)="onFilterUpdate('endDate', $event); bothDatesEntered && reload()"
                        ></s25-datepicker>
                    }

                    @if (hasActiveFilters()) {
                        <button class="text-btn" (click)="clearAllFilters()" type="button">Clear filters</button>
                    }
                </div>
            </div>

            @if (hasActiveFilters()) {
                <div class="active-filters">
                    <span class="filter-label">Active filters:</span>
                    @if (filterOptions().searchTerm) {
                        <span class="filter-pill">
                            "{{ filterOptions().searchTerm }}"
                            <button (click)="onFilterUpdate('searchTerm', ''); reload()" type="button">×</button>
                        </span>
                    }
                    @if (filterOptions().status !== "all") {
                        <span class="filter-pill">
                            {{ getStatusLabel() }}
                            <button (click)="onFilterUpdate('status', 'all'); reload()" type="button">×</button>
                        </span>
                    }
                    @if (filterOptions().dateRange !== "all") {
                        <span class="filter-pill">
                            {{ getDateRangeLabel() }}
                            <button (click)="onFilterUpdate('dateRange', 'all'); reload()" type="button">×</button>
                        </span>
                    }
                    @if (adminMode() && selectedOrganization()) {
                        <span class="filter-pill">
                            {{ selectedOrganization()!.itemName }}
                            <button (click)="clearOrganizationFilter()" type="button">×</button>
                        </span>
                    }
                    @if (adminMode() && selectedContact()) {
                        <span class="filter-pill">
                            {{ selectedContact()!.itemName }}
                            <button (click)="clearContactFilter()" type="button">×</button>
                        </span>
                    }
                </div>
            }

            @if (loading()) {
                <div class="loading-container">
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                </div>
            } @else if (serviceHours.resource.error()) {
                <div class="message-container error">
                    <s25-ng-icon [type]="'alertCircle'" [size]="24" [ariaLabel]="'Error'" />
                    <div>
                        <p class="message-title">Unable to load service hours</p>
                        <p class="message-text">{{ serviceHours.resource.error()?.message }}</p>
                    </div>
                    <button class="text-btn" (click)="reload()" type="button">Try again</button>
                </div>
            } @else if (filteredHours().length === 0) {
                <div class="message-container">
                    <s25-ng-icon [type]="'clock'" [size]="32" [ariaLabel]="false" />
                    <div>
                        <p class="message-title">No service hours found</p>
                        <p class="message-text">
                            @if (hasActiveFilters()) {
                                Try adjusting your filters
                            } @else if (adminMode()) {
                                No service hours have been logged yet
                            } @else {
                                Log your service hours to get started
                            }
                        </p>
                    </div>
                    @if (hasActiveFilters()) {
                        <button class="text-btn" (click)="clearAllFilters()" type="button">Clear filters</button>
                    }
                </div>
            } @else {
                <div class="hours-content">
                    <s25-ng-icon-button [type]="'refresh'" (clicked)="reload()" [ariaLabel]="'Refresh'" />
                    <p class="results-count">Showing {{ filteredHours().length }} entries</p>

                    <div class="hours-list">
                        @for (entry of filteredHours(); track entry.serviceHourId) {
                            <service-hour-entry
                                [entry]="entry"
                                [canApprove]="canApprove()"
                                [showContact]="adminMode() || itemTypeId() !== itemIdTypes.Contact"
                                (reviewCompleted)="onReviewCompleted()"
                            ></service-hour-entry>
                        }
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .my-hours-container {
            max-width: 1400px;
            margin: 0 auto;
        }

        .hours-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border);
            gap: 1em;
        }

        .header-left {
            display: flex;
            align-items: baseline;
            gap: 1rem;
        }

        .page-title {
            font-size: 1.75rem;
            font-weight: 600;
            color: var(--color--text-strong);
            margin: 0;
        }

        :host ::ng-deep .aw-button--outline {
            display: flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.5rem 1rem;
        }

        s25-ng-button {
            margin-left: auto;
        }

        .stats-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
            margin-bottom: 2rem;
        }

        .stat-card {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1.25rem;
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            transition: all var(--transition-medium) ease;
        }

        .stat-card:hover {
            border-color: var(--color--border-hover);
            transform: translateY(-1px);
        }

        .stat-card.approved ::ng-deep svg {
            color: var(--color--objects-event);
        }

        .stat-card.pending ::ng-deep svg {
            color: var(--color--text-warning);
        }

        .stat-card.denied ::ng-deep svg {
            color: var(--color--danger);
        }

        .stat-card.total ::ng-deep svg {
            color: var(--cn-blue);
        }

        .stat-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
        }

        .stat-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .stat-label {
            font-size: 0.75rem;
            color: var(--color--text-muted);
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }

        .filters-container {
            display: flex;
            gap: 1rem;
            margin-bottom: 1.5rem;
            flex-wrap: wrap;
        }

        .search-wrapper {
            flex: 1;
            min-width: 240px;
            position: relative;
        }

        .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted);
            pointer-events: none;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.5rem;
            border: 1px solid var(--color--form-border);
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color var(--transition-medium) ease;
            background: var(--color--form-bg);
            color: var(--color--text);
        }

        .search-input:focus {
            outline: none;
            border-color: var(--color--form-border-hover);
            box-shadow: var(--shadow--form-focus);
        }

        .clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius);
            transition: all var(--transition-medium) ease;
        }

        .clear-btn:hover {
            color: var(--color--text);
            background: var(--color--surface-2-bg);
        }

        .filter-controls {
            display: flex;
            gap: 0.75rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .admin-filter-picker {
            min-width: 200px;
        }

        .admin-filter-picker ::ng-deep .c-input {
            width: 100%;
        }

        .filter-select,
        .date-input {
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--form-border);
            border-radius: 6px;
            font-size: 0.875rem;
            cursor: pointer;
            transition: border-color var(--transition-medium) ease;
            background: var(--color--form-bg);
            color: var(--color--text);
        }

        .filter-select {
            padding-right: 2rem;
            appearance: none;
            background: var(--color--form-bg)
                url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")
                no-repeat right 0.5rem center;
            background-size: 16px;
            min-width: 140px;
        }

        .filter-select:focus,
        .date-input:focus {
            outline: none;
            border-color: var(--color--form-border-hover);
            box-shadow: var(--shadow--form-focus);
        }

        .text-btn {
            background: none;
            border: none;
            color: var(--color--text-muted);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            transition: all var(--transition-medium) ease;
        }

        .text-btn:hover {
            color: var(--color--text);
            background: var(--color--surface-2-bg);
        }

        .active-filters {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            margin-bottom: 1rem;
            font-size: 0.875rem;
        }

        .filter-label {
            color: var(--color--text-muted);
        }

        .filter-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            background: var(--color--surface-2-bg);
            border-radius: 4px;
            font-size: 0.8125rem;
            color: var(--color--text);
        }

        .filter-pill button {
            background: none;
            border: none;
            color: var(--color--text-muted);
            cursor: pointer;
            padding: 0;
            font-size: 1.125rem;
            line-height: 1;
            display: flex;
            align-items: center;
            transition: color var(--transition-medium) ease;
        }

        .filter-pill button:hover {
            color: var(--color--text);
        }

        .results-count {
            margin: 0 0 1rem 0;
            color: var(--color--text-muted);
            font-size: 0.875rem;
        }

        .hours-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .loading-container {
            display: flex;
            justify-content: center;
            padding: 3rem;
        }

        .message-container {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 2rem;
            background: var(--color--surface-2-bg);
            border-radius: 8px;
            margin-top: 2rem;
        }

        .message-container.error {
            background: var(--color--surface-bg-error);
        }

        ::ng-deep .message-container > s25-ng-icon:first-child svg {
            color: var(--color--border-bold);
            flex-shrink: 0;
        }

        ::ng-deep .message-container.error > s25-ng-icon:first-child svg {
            color: var(--color--danger);
        }

        ::ng-deep .my-hours-container .sidebar-content {
            padding-top: 0 !important;
        }

        ::ng-deep .filter-controls s25-datepicker .c-input {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--form-border) !important;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color var(--transition-medium) ease;
            background: var(--color--form-bg);
            height: unset;
        }

        .message-container > div {
            flex: 1;
        }

        .message-title {
            font-weight: 500;
            color: var(--color--text-strong);
            margin: 0 0 0.25rem 0;
        }

        .message-text {
            color: var(--color--text-muted);
            font-size: 0.875rem;
            margin: 0;
        }

        .message-container.error .message-title {
            color: var(--color--danger);
        }

        .message-container.error .message-text {
            color: var(--color--danger);
        }

        s25-ng-icon-button {
            float: right;
        }

        @media (max-width: 768px) {
            .hours-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 1rem;
            }

            .aw-button--outline {
                width: 100%;
                justify-content: center;
            }

            .stats-summary {
                grid-template-columns: 1fr 1fr;
            }

            .filters-container {
                flex-direction: column;
            }

            .filter-controls {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
            }

            .filter-select,
            .date-input {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SidebarStore],
    imports: [
        CommonModule,
        FormsModule,
        S25LoadingInlineModule,
        S25IconComponent,
        S25ModalModule,
        ServiceHoursFormComponent,
        S25SidebarComponent,
        S25IconButtonComponent,
        S25DatepickerModule,
        ServiceHourEntryComponent,
        S25ButtonComponent,
        S25DropdownPaginatedModule,
        S25SelectEnterOpensDirective,
    ],
})
export class SeServiceHoursComponent {
    adminMode = input(false);
    itemTypeId = input<Item.Ids>();

    entries = viewChildren(ServiceHourEntryComponent);

    protected sidebarStore = inject(SidebarStore);
    private stateStore = inject(StateStore);
    private orgSecStore = inject(SeOrgSecurityStore, { optional: true });

    queueUpdated = output<void>();
    private userStore = inject(UserStore);
    private serviceHoursService = inject(ServiceHoursService);

    protected itemIdTypes = Item.Ids;

    readonly itemId = computed(() => {
        if (this.adminMode()) {
            return undefined;
        }
        const itemTypeId = this.itemTypeId(),
            seItemTypeId = this.stateStore.seObjTypeId();

        if (itemTypeId === seItemTypeId) {
            return this.stateStore.seItemId() ?? undefined;
        }
    });

    constructor() {
        // Organization hours is a sibling of org home and shares SeOrgSecurityStore.
        // Rights are keyed by the store org, so this page has to point the store at its own org.
        effect(() => {
            const orgSecStore = this.orgSecStore;
            if (!orgSecStore || this.adminMode() || this.itemTypeId() !== Item.Ids.Organization) {
                return;
            }
            const orgId = this.itemId();
            if (!orgId || orgSecStore.orgId() === orgId) {
                return;
            }
            orgSecStore.setOrgId(orgId);
        });
    }

    readonly canLogHours = computed(() => {
        if (this.adminMode()) {
            return false;
        }
        const orgId = this.itemId();
        if (orgId && this.orgSecStore) {
            if (this.orgSecStore.orgId() !== orgId) {
                return false;
            }
            return !!this.orgSecStore.canLogServiceHours();
        }
        return true;
    });

    readonly canApprove = computed(() => {
        if (this.adminMode()) {
            return false;
        }
        const orgId = this.itemId();
        if (
            this.itemTypeId() === Item.Ids.Organization &&
            this.orgSecStore &&
            orgId &&
            this.orgSecStore.orgId() === orgId
        ) {
            return !!this.orgSecStore.canApproveServiceHours();
        }
        return false;
    });

    readonly selectedOrganization = signal<DropDownItem | undefined>(undefined);
    readonly selectedContact = signal<DropDownItem | undefined>(undefined);

    filterOptions = signal<ServiceHoursViewFilter>({
        status: "all",
        dateRange: "all",
        startDate: undefined,
        endDate: undefined,
        searchTerm: "",
    });

    serviceHours = s25Resource({
        if: () => this.adminMode() || !!this.itemTypeId(),
        params: () => ({
            adminMode: this.adminMode(),
            itemTypeId: this.itemTypeId(),
            itemId: this.itemId(),
            filter: this.buildApiFilter(),
        }),
        loader: ({ params }) => {
            const loadPromise = params.adminMode
                ? this.serviceHoursService.getAdminServiceHours(params.filter)
                : this.serviceHoursService.getServiceHours(params.itemTypeId!, params.itemId, params.filter);

            return loadPromise.catch((err) => {
                if (!err?.message) {
                    throw new Error("Failed to load service hours", { cause: err });
                }
                throw err;
            });
        },
        defaultValue: [],
    });

    filteredHours = computed<ServiceHourEntry[]>(() => {
        const hours = this.serviceHours.value() ?? [];
        return hours
            .map((h) => ({
                serviceHourId: h.serviceHourId,
                contId: h.contId,
                contactName: h.contact?.fullName ?? h.contactName ?? "",
                organizationId: h.organizationId,
                organizationName: h.organizationName || "",
                eventId: h.eventId,
                eventName: h.eventName,
                activityName: h.activityName,
                serviceDate: h.serviceDate,
                hours: h.hours,
                status: h.status,
                description: h.description,
                verifiedByContId: h.verifiedByContId,
                verifiedByName: h.verifiedByName,
                denialReason: h.denialReason,
                createDt: h.createDt,
                createUser: h.createUser,
                lastModDt: h.lastModDt,
                lastModUser: h.lastModUser,
            }))
            .sort(compareServiceHourEntries);
    });

    loading = computed(() => this.serviceHours.resource.isLoading());

    totalHours = computed(() => {
        return this.filteredHours().reduce((sum, h) => this.roundHours(sum + h.hours), 0);
    });

    totalApprovedHours = computed(() => {
        return this.filteredHours()
            .filter((h) => h.status === ServiceHoursStatus.Approved)
            .reduce((sum, h) => this.roundHours(sum + h.hours), 0);
    });

    totalPendingHours = computed(() => {
        return this.filteredHours()
            .filter((h) => h.status === ServiceHoursStatus.Pending)
            .reduce((sum, h) => this.roundHours(sum + h.hours), 0);
    });

    totalDeniedHours = computed(() => {
        return this.filteredHours()
            .filter((h) => h.status === ServiceHoursStatus.Denied)
            .reduce((sum, h) => this.roundHours(sum + h.hours), 0);
    });

    reload() {
        this.serviceHours.resource.reload();
        this.emitQueueUpdatedIfApprover();
    }

    protected onReviewCompleted(): void {
        this.emitQueueUpdatedIfApprover();
    }

    private emitQueueUpdatedIfApprover(): void {
        if (this.canApprove()) {
            this.queueUpdated.emit();
        }
    }

    onDateRangeChange() {
        if (this.filterOptions().dateRange !== "custom") {
            this.filterOptions.update((filters) => ({ ...filters, startDate: undefined, endDate: undefined }));
        }
        this.reload();
    }

    hasActiveFilters = computed(() => {
        const filters = this.filterOptions();
        return (
            filters.status !== "all" ||
            filters.dateRange !== "all" ||
            filters.searchTerm !== "" ||
            !!filters.organizationId ||
            !!filters.contactId
        );
    });

    clearAllFilters() {
        this.selectedOrganization.set(undefined);
        this.selectedContact.set(undefined);
        this.filterOptions.set({
            status: "all",
            dateRange: "all",
            startDate: undefined,
            endDate: undefined,
            searchTerm: "",
        });
        this.reload();
    }

    onOrganizationFilterChange(organization: DropDownItem | undefined) {
        this.selectedOrganization.set(organization);
        this.filterOptions.update((filters) => ({
            ...filters,
            organizationId: organization?.itemId ? Number(organization.itemId) : undefined,
        }));
        this.reload();
    }

    onContactFilterChange(contact: DropDownItem | undefined) {
        this.selectedContact.set(contact);
        this.filterOptions.update((filters) => ({
            ...filters,
            contactId: contact?.itemId ? Number(contact.itemId) : undefined,
        }));
        this.reload();
    }

    clearOrganizationFilter() {
        this.onOrganizationFilterChange(undefined);
    }

    clearContactFilter() {
        this.onContactFilterChange(undefined);
    }

    private buildApiFilter(): ServiceHourFilter {
        const filters = this.filterOptions();
        return {
            status: filters.status,
            dateRange: filters.dateRange,
            startDate: S25Util.date.toS25ISODateStr(filters.startDate) ?? undefined,
            endDate: S25Util.date.toS25ISODateStr(filters.endDate) ?? undefined,
            searchTerm: filters.searchTerm || undefined,
            organizationId: filters.organizationId,
            contactId: filters.contactId,
        };
    }

    formatDate(dateStr: string | number | Date | undefined): string {
        if (S25Util.isUndefined(dateStr)) return "";
        return S25Datefilter.transform(S25Util.date.parse(dateStr), this.userStore.dateFormat()!);
    }

    getStatusLabel(): string {
        const labels: Record<string, string> = {
            all: "All Status",
            approved: "Approved",
            pending: "Pending",
            denied: "Denied",
        };
        return labels[this.filterOptions().status] || "All Status";
    }

    getDateRangeLabel(): string {
        const labels: Record<string, string> = {
            all: "All Time",
            week: "This Week",
            month: "This Month",
            semester: "This Semester",
            year: "This Year",
            custom:
                this.filterOptions().startDate && this.filterOptions().endDate
                    ? `${this.formatDate(this.filterOptions().startDate)} - ${this.formatDate(this.filterOptions().endDate)}`
                    : "Custom Range",
        };
        return labels[this.filterOptions().dateRange] || "All Time";
    }

    onFilterUpdate(
        prop: keyof ServiceHoursViewFilter,
        value: ServiceHoursViewFilter[keyof ServiceHoursViewFilter] | null | undefined,
    ) {
        if (S25Util.isUndefined(value)) return;
        this.filterOptions.update((filters) => ({ ...filters, [prop]: value }));
    }

    @Debounce(500)
    onKeywordSearch() {
        this.reload();
    }

    roundHours(value: number) {
        if (!value) return 0;
        return parseFloat(value.toPrecision(12));
    }

    @Bind
    async onExport() {
        if (!this.entries()?.length) return;

        const columnMap: Partial<Record<keyof ServiceHourEntry, string>> = {
            contactName: "Contact",
            activityName: "Activity",
            createDt: "Date Created",
            createUser: "Logged By",
            organizationName: "Organization",
            eventName: "Event",
            serviceDate: "Service Date",
            hours: "Hours",
            description: "Description",
            status: "Status",
            denialReason: "Denial Reason",
            verifiedByName: "Verified By",
        };
        let name: string | undefined;
        const data: SheetEntry[] = this.entries()
            .map((entryComp) => {
                const entry = entryComp.entry();
                if (!entry) return;

                if (this.adminMode()) {
                    name ??= "all";
                } else if (this.itemTypeId() === Item.Ids.Organization) {
                    name ??= entry.organizationName ?? "";
                } else {
                    name ??= entry.contactName ?? "";
                }

                const sheetEntry: SheetEntry = {};
                for (const [key, header] of Object.entries(columnMap)) {
                    sheetEntry[header] = <string>entry[<keyof ServiceHourEntry>key] || "";
                }
                return sheetEntry;
            })
            .filter((entry) => !!entry);

        await SpreadSheetService.downloadExcel(data, this.getFileName(name), this.getSheetName(name));
    }

    getFileName(name: string | undefined) {
        if (this.adminMode()) {
            return "all-service-hours.xlsx";
        }
        return `${
            name
                ? name
                      .split(" ")
                      .map((word) => word.toLowerCase())
                      .join("-") + "-"
                : ""
        }service-hours.xlsx`;
    }

    getSheetName(name: string | undefined) {
        if (this.adminMode()) {
            return "All Service Hours";
        }
        if (!name) return "";
        return `Service Hours${name ? " for " + name : ""}`;
    }
}
